import {
  Badge,
  Button,
  Card,
  ChevronLeftIcon,
  Dialog,
  Heading,
  Image,
  majorScale,
  Pane,
  Pill,
  Switch,
  Text,
  toaster,
} from 'evergreen-ui'
import Parser from 'html-react-parser'
import { FC, useEffect, useState } from 'react'
import Lightbox from 'yet-another-react-lightbox'
import 'yet-another-react-lightbox/styles.css'
import { CONSTANTS } from '../../constants'
import { useDeletePostMutation } from '../../services/posts/posts.service'
import { Post, User } from '../../types'
import { convertToFriendlyDate } from '../../utils/convert-date'
import { generateUsername } from '../../utils/username'
import CommentsContainer from '../CommentsContainer/CommentsContainer'
import {
  ContentEditorDetails,
} from '../ContentEditor/ContentEditor'
import EditableContextMenu from '../EditableContextMenu/EditableContextMenu'
import RatingWithSurvey from '../RatingWithSurvey/RatingWithSurvey'

import { useNavigate } from 'react-router-dom'
import WysiwygEditor from '../WysiwygEditor/WysiwygEditor'
import styles from './PostDetailCard.module.scss'

interface PostDetailCardProps {
  post: Post
  user?: User
  isEditSaveSuccess: boolean
  editPost: (content: ContentEditorDetails) => void
  isSavingEdit: boolean
  setUserSubscribed: (subscribed: boolean) => void
  setUserUnsubscribed: (unsubscribed: boolean) => void
}

const PostDetailCard: FC<PostDetailCardProps> = (
  props: PostDetailCardProps,
) => {
  const [isEditing, setIsEditing] = useState<boolean>(false)
  const [lightboxIndex, setLightboxIndex] = useState<number>(-1)
  const [isUserSubscribed, setIsUserSubscribed] = useState<boolean>(false)
  const [isUserUnsubscribed, setIsUserUnsubscribed] = useState<boolean>(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false)
  let navigate = useNavigate()

  // Delete post mutation
  const [deletePost, { isLoading: isDeletingPost }] = useDeletePostMutation()

  useEffect(() => {
    if (props.isEditSaveSuccess) {
      setIsEditing(false)
    }
  }, [props.isEditSaveSuccess])

  useEffect(() => {
    setIsUserSubscribed(props.post?.meta?.isSubscribed ?? false);
  }, [props.post?.meta?.isSubscribed]);

  useEffect(() => {
    setIsUserUnsubscribed(props.post?.meta?.isUnsubscribed ?? false);
  }, [props.post?.meta?.isUnsubscribed]);

  // Navigate back to patient videos page
  const goBack = () => {
    navigate(CONSTANTS.ROUTES.COMMUNITY);
  }

  const handleSubscriptionToggle = async () => {
    props.setUserSubscribed(!isUserSubscribed);
    setIsUserSubscribed(e => !e);
  }

  const handleUnsubscriptionToggle = async () => {
    props.setUserUnsubscribed(!isUserUnsubscribed);
    setIsUserUnsubscribed(e => !e);
  }

  const handleDeletePost = async () => {
    try {
      // Navigate immediately to prevent refetch attempts
      navigate('/community')
      await deletePost(props.post.id).unwrap()
      toaster.success('Post deleted successfully')
    } catch (error) {
      toaster.danger('Error deleting post')
    }
  }

  return (
    <Pane>
      <Pane marginBottom={majorScale(2)}>
        <Heading size={700} className="crumb parent" onClick={() => goBack()}>
          Community
        </Heading>
        <Heading size={900} className="crumb child">
          {props.post?.title}
        </Heading>
      </Pane>
      <Pane
        display="flex"
        flexDirection="row"
        justifyContent="space-between"
        alignItems="flex-end"
      >
        <Button
          appearance="secondary"
          onClick={() => goBack()}
          marginBottom={majorScale(3)}
        >
          <ChevronLeftIcon marginRight={majorScale(1)}></ChevronLeftIcon>Back to Community
        </Button>
        {/*<Pane
          display="flex"
          flexDirection="row"
          justifyContent="flex-end"
        >
          <Text marginRight={majorScale(1)}>{isUserSubscribed ? "Subscribed" : "Not Subscribed"}</Text>
          <Switch checked={isUserSubscribed} onChange={handleSubscriptionToggle}/>
        </Pane>*/}
        <Pane
          display="flex"
          flexDirection="row"
          justifyContent="flex-end"
        >
          <Text marginRight={majorScale(1)}>{!isUserUnsubscribed ? "Subscribed" : "Unsubscribed"}</Text>
          <Switch checked={!isUserUnsubscribed} onChange={handleUnsubscriptionToggle}/>
        </Pane>
      </Pane>
      <Card
        border="default"
        background="white"
        borderRadius={10}
        padding={majorScale(2)}
      >
        <Pane
          display="flex"
          flexDirection="row"
          justifyContent="space-between"
          alignItems="flex-start"
        >
          <Pane>
            <Pane marginBottom={majorScale(4)}>
              {props.post.tags?.map((tag) => (
                <Badge key={tag.id} color={tag.tag.color}>
                  {tag.tag.name}
                </Badge>
              ))}
            </Pane>
          </Pane>
          {/* Only show the edit menu if it is the logged in user */}
          {props.user?.id === props.post.user_id ? (
            <EditableContextMenu
              editable={true}
              edit={() => {
                setIsEditing(!isEditing)
              }}
              deletable={true}
              delete={() => {
                setIsDeleteDialogOpen(true)
              }}
            ></EditableContextMenu>
          ) : null}
        </Pane>

        <Pane>
          {isEditing ? (
            <WysiwygEditor
              initialValue={props.post.content}
              onUpdateContent={(newContent: string) => {
                props.editPost({content: newContent})
                setIsEditing(false)
              }}
            />
          ) : (
            <Text
              display="block"
              marginBottom={majorScale(2)}
              fontSize={'1rem'}
              whiteSpace="pre-line"
              lineHeight={1.5}
            >
              {Parser(props.post.content)}
            </Text>
          )}
        </Pane>

        {(props.post.userUploads?.length ?? 0) > 0 ? (
          <Pane
            display="flex"
            flexDirection="row"
            gap={majorScale(2)}
            marginBottom={majorScale(2)}
          >
            {props.post.userUploads?.map((upload, index) => (
              <Pane
                elevation={1}
                key={upload.id}
                hoverElevation={2}
                cursor={'pointer'}
                onClick={() => setLightboxIndex(index)}
              >
                <Image
                  className={styles.Thumbnail}
                  src={upload.url}
                  width={100}
                  height={100}
                ></Image>
              </Pane>
            ))}
          </Pane>
        ) : null}

        <Pane>
          <RatingWithSurvey
            route={CONSTANTS.ROUTES.COMMUNITY}
            upvotes={props.post.meta?.upvotes}
            postId={props.post.id}
            userId={props.user?.id ?? ''}
            ratings={props.post.ratings}
            displayText="This post provides information that will improve my practice: "
          ></RatingWithSurvey>
        </Pane>
      </Card>
      <Pane
        display="flex"
        alignItems="center"
        width="100%"
        flexDirection="row"
        marginY={majorScale(2)}
        paddingX={majorScale(2)}
      >
        <Text
          color="#696f8c"
          textTransform="uppercase"
          display="inline-flex"
          alignItems="center"
        >
          {generateUsername(props.post.user)}
          <Pill color="neutral">Author</Pill>&nbsp;&nbsp;•&nbsp;&nbsp;
          {convertToFriendlyDate(props.post.created_at)}
        </Text>
      </Pane>
      <CommentsContainer
        contextId={props.post.id}
        defaultSort={CONSTANTS.SORT.POST_DETAIL.DEFAULT}
        sortOptions={CONSTANTS.SORT.POST_DETAIL.OPTIONS}
        contextType="COMMUNITY_POST"
        contextAuthorId={props.post.user_id}
        setIsUserSubscribedToCurrentPost={props.setUserSubscribed}
      />
      <Lightbox
        open={lightboxIndex > -1}
        index={lightboxIndex}
        close={() => setLightboxIndex(-1)}
        slides={props.post.userUploads?.map((u) => {
          return { src: u.url }
        })}
      ></Lightbox>

      {/* Delete confirmation dialog */}
      <Dialog
        isShown={isDeleteDialogOpen}
        title="Delete Post"
        intent="danger"
        onCloseComplete={() => setIsDeleteDialogOpen(false)}
        confirmLabel="Delete"
        onConfirm={handleDeletePost}
        isConfirmLoading={isDeletingPost}
      >
        <Text>
          Are you sure you want to delete this post? This action cannot be undone.
        </Text>
      </Dialog>
    </Pane>
  )
}

export default PostDetailCard
